import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import {
	CallActivityPerformanceFragment,
	CustomerSatisfactionStoreFragment,
	LongevityFragment,
	TopPerformerBonusFragment,
	UserFullNameFragment,
	BonusDefinitionFragment,
	CommissionTiersFragment,
	MetricFragment,
	InputValueFragment,
	VariableOperationsGpsBonusSetupFragment,
} from "@PayPlans/api/fragments/commons";

import {
	CashAndLeaseBumpFragment,
	CITBonusesFragment,
	DefaultCommissionsFragment,
	DepartmentOverrideNetIncomeFragment,
	MaxPayoutFragment,
	ProductBonusesFragment,
	PVRQualifierFragment,
} from "@PayPlans/api/fragments/managers/finance";

// Retrieve finance pay plans list
export async function getFinancePayPlans(locationId, position) {
	if (locationId) {
		const query = gql`
			query getFinancePayPlans($locationId: String!, $position: String!) {
				getFinancePayPlans(locationId: $locationId, position: $position) {
					_id
					name
					position
					locked
					available
				}
			}
		`;

		const variables = {
			locationId,
			position,
		};

		try {
			const response = await apiInstance.post("/graphql", { query, variables });
			return response.data.data?.getFinancePayPlans || null;
		} catch (error) {
			return error;
		}
	}

	console.error("Bad parameters for getFinancePayPlans");
	return [];
}

// Retrieve finance pay plans
export async function getFinancePayPlan(locationId, id, position) {
	if (locationId && id) {
		const query = gql`
			query getFinancePayPlan($locationId: String!, $id: String!, $position: String!) {
				getFinancePayPlan(locationId: $locationId, id: $id, position: $position) {
					_id
					locationId
					name
					position
					backendPVRComputation
					default {
						...FinanceDefaultCommissions
					}
					productBonuses {
						...ProductBonuses
					}
					cashAndLeaseBump {
						...CashAndLeaseBump
					}
					callActivityPerformance {
						...CallActivityPerformance
					}
					cit {
						...CITBonuses
					}
					customerSatisfaction {
						...CustomerSatisfactionStore
					}
					departmentOverrideNetIncome {
						...DepartmentOverrideNetIncome
					}
					pvrQualifier {
						...PVRQualifier
					}
					topSalesBonus {
						...TopPerformerBonus
					}
					maxPayout {
						...MaxPayout
					}
					longevity {
						...Longevity
					}
					variableOperationsGpsBonus {
						...VariableOperationsGpsBonusSetup
					}
					locked
					available
					formatVersion
					createdAt
					createdBy {
						...UserFullName
					}
					updatedAt
					updatedBy {
						...UserFullName
					}
				}
			}
			${DefaultCommissionsFragment}
			${ProductBonusesFragment}
			${CashAndLeaseBumpFragment}
			${CallActivityPerformanceFragment}
			${CITBonusesFragment}
			${CustomerSatisfactionStoreFragment}
			${DepartmentOverrideNetIncomeFragment}
			${PVRQualifierFragment}
			${TopPerformerBonusFragment}
			${MaxPayoutFragment}
			${LongevityFragment}
			${UserFullNameFragment}
			${BonusDefinitionFragment}
			${CommissionTiersFragment}
			${MetricFragment}
			${InputValueFragment}
			${VariableOperationsGpsBonusSetupFragment}
		`;

		const variables = {
			locationId,
			id,
			position,
		};

		try {
			const response = await apiInstance.post("/graphql", { query, variables });
			return response.data.data?.getFinancePayPlan || null;
		} catch (error) {
			return error;
		}
	}

	return {
		message: `Some error occurred`,
	};
}

export async function getFinancePayPlansSidebarTree(locationId, position, dontGetAdditionalPositions = false) {
	const query = gql`
		query getFinancePayPlansSidebarTree($locationId: String!, $position: String!, $dontGetAdditionalPositions: Boolean) {
			getFinancePayPlansSidebarTree(
				locationId: $locationId
				position: $position
				dontGetAdditionalPositions: $dontGetAdditionalPositions
			) {
				defaultPayPlans {
					defaultPayPlan {
						_id
						name
						position
						pdfAvailability
					}
					newVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
					preOwnedVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
				}
				available {
					_id
					name
					position
					pdfAvailability
				}
				inProgress {
					_id
					name
					position
					pdfAvailability
				}
				currentlyAssigned {
					_id
					name
					position
					pdfAvailability
				}
				previouslyAssigned {
					_id
					name
					position
					pdfAvailability
				}
			}
		}
	`;

	const variables = {
		locationId,
		position,
		dontGetAdditionalPositions,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getFinancePayPlansSidebarTree || [];
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Retrieve sales pay plans full and partial matches
export async function getFinancePayPlanMatches(locationId, payPlanId, position) {
	if (locationId && payPlanId) {
		const query = gql`
			query getFinancePayPlanMatches($locationId: String!, $id: String!, $position: String!) {
				getFinancePayPlanMatches(id: $id, locationId: $locationId, position: $position) {
					locationId
					payPlanId
					fullMatchList
					partialMatchList
				}
			}
		`;

		const variables = {
			locationId,
			position,
			id: payPlanId,
		};

		try {
			const response = await apiInstance.post("/graphql", { query, variables });
			return response.data.data?.getFinancePayPlanMatches;
		} catch (err) {
			console.error(err);
			return null;
		}
	}

	return {
		locationId,
		payPlanId,
		fullMatchList: [],
		partialMatchList: [],
	};
}
